import React from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { KpiCard } from '../../../components/financeiro';
import { formatCurrency } from '../../../utils/money';
import type { ResumoFinanceiro, PontoSerieFinanceira, PeriodoFinanceiro } from '../../../types/financeiro';

// Recebe tudo por props vindas do hub — a page é a camada de orquestração; a aba não busca
// nada (ai/architecture.core.md §5, "Components MUST NOT fetch data").

export interface ResumoTabProps {
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  onRetry: () => void;
  periodo: PeriodoFinanceiro;
}

function formatDataBr(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY');
}

const ResumoTab: React.FC<ResumoTabProps> = ({
  resumo,
  serie,
  loading,
  error,
  resumoIndisponivel,
  onRetry,
  periodo,
}) => {
  // AC-12: falha de rede — mensagem amigável + ação de repetir, sem crash e sem catch
  // silencioso.
  if (error) {
    return (
      <div className="text-center py-5" data-testid="resumo-erro">
        <p className="text-danger mb-3">
          Não foi possível carregar o resumo financeiro. Tente novamente.
        </p>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    );
  }

  // AC-12: meuResumoFinanceiro retornando null SEM erro (contrato nullable da F-1) — estado
  // tratado, distinto do erro de rede.
  if (resumoIndisponivel) {
    return (
      <div className="text-center py-5" data-testid="resumo-indisponivel">
        <p className="text-muted mb-3">O resumo financeiro não está disponível no momento.</p>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (loading || !resumo) {
    return <div className="text-center py-5">Carregando resumo financeiro...</div>;
  }

  // Barra + eixo de tempo real + timestamp local: as três escolhas são acopladas e NÃO são
  // estéticas — cada uma fecha uma armadilha do Apex que fazia o gráfico mentir (spline
  // inventando valores entre dias esparsos, `categories` string achatando a distância entre
  // datas, ISO date-only virando o dia anterior em UTC-3). Rationale completo, com sintoma e
  // correção de cada uma, em `ai/libs/APEXCHARTS.md` §1 e na tabela "Armadilhas conhecidas".
  const chartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    dataLabels: { enabled: false },
    xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
    yaxis: { labels: { formatter: (valor: number) => formatCurrency(valor) } },
    tooltip: { x: { format: 'dd/MM/yyyy' } },
    plotOptions: { bar: { columnWidth: '55%' } },
  };
  const chartSeries = [
    {
      name: 'Pago por dia',
      data: serie.map((ponto) => ({ x: dayjs(ponto.data).valueOf(), y: ponto.valor })),
    },
  ];

  return (
    <div>
      <div className="mb-3">
        <h5 className="fw-700 mb-1">Resumo de inscrições</h5>
        <span className="text-grey-500 font-xsss">
          Estes totais consideram apenas inscrições — pagamentos de quadras não entram aqui.
        </span>
      </div>

      {/* Rótulos na PERSPECTIVA DO ATLETA: aqui o dinheiro SAI. `totalRecebido` é o nome do campo
          no contrato do backend (que serve também os relatórios de liga/plataforma, onde a receita
          de fato ENTRA) — para o atleta o mesmo número é o que ele pagou, e chamá-lo de "recebido"
          inverte o sentido. `totalNaoRealizado` (CANCELADO/RECUSADO/EXPIRADO) não é exibido: não é
          dívida nem gasto, só ruído para quem está tentando saber o que deve e o que já pagou. */}
      <div className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Total pago"
            valor={resumo.totalRecebido}
            legenda="Inscrições que você já pagou"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Total em aberto"
            valor={resumo.totalEmAberto}
            legenda="Inscrições ainda a pagar"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Total estornado"
            valor={resumo.totalEstornado}
            legenda="Valores devolvidos a você"
          />
        </div>
      </div>

      {serie.length === 0 ? (
        <div className="text-center py-5" data-testid="resumo-serie-vazia">
          <p className="mb-3">
            Nenhum lançamento entre {formatDataBr(periodo.inicio)} e {formatDataBr(periodo.fim)}.
          </p>
          <p className="text-grey-500">Tente ampliar o período para ver mais lançamentos.</p>
        </div>
      ) : (
        <div className="card border-0 shadow-xss rounded-xxl p-3">
          <Chart options={chartOptions} series={chartSeries} type="bar" width="100%" />
        </div>
      )}
    </div>
  );
};

export default ResumoTab;
